import { useCallback, useEffect, useMemo, useState } from 'react'
import { ArrowDown, ArrowUp, ArrowUpDown, Search } from 'lucide-react'
import { fetchCustomers } from '@/api/customers'
import { fetchThreatDownDetections } from '@/api/threatdown'
import { fetchThreatDownSyncStatus } from '@/api/sync'
import { CustomerFilter } from '@/components/CustomerFilter'
import { AiQueryPanel } from '@/components/ai/AiQueryPanel'
import { buildDetectionColumns } from '@/components/threatdown/detectionTableUtils'
import { ThreatDownNav } from '@/components/threatdown/ThreatDownNav'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/auth/AuthContext'
import { formatDateTime } from '@/lib/formatAgent'
import { cn } from '@/lib/utils'

const PAGE_SIZE = 50

const DETECTION_EXAMPLES = [
  'How many Phishing detections are there?',
  'Which endpoints have the most blocked events?',
  'What does category MWAC mean in these detections?',
  'Summarise detections from the last week.',
]

const SORTABLE = new Set([
  'hostname',
  'customer',
  'scanned_at',
  'threat_name',
  'category',
  'action_taken',
  'account_name',
  'path',
  'process_name',
])

function SortHeader({ column, label, sortBy, sortDir, onSort, className }) {
  const active = sortBy === column
  const sortable = SORTABLE.has(column)
  if (!sortable) {
    return <th className={cn('px-4 py-3 font-medium', className)}>{label}</th>
  }
  return (
    <th
      className={cn(
        'px-4 py-3 font-medium cursor-pointer select-none hover:text-foreground',
        className,
      )}
      onClick={() => onSort(column)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active ? (
          sortDir === 'asc' ? (
            <ArrowUp className="h-3.5 w-3.5 shrink-0" />
          ) : (
            <ArrowDown className="h-3.5 w-3.5 shrink-0" />
          )
        ) : (
          <ArrowUpDown className="h-3.5 w-3.5 shrink-0 opacity-40" />
        )}
      </span>
    </th>
  )
}

export function ThreatDownDetections() {
  const { isCustomer, canFilterCustomers } = useAuth()
  const [customers, setCustomers] = useState([])
  const [customerIds, setCustomerIds] = useState([])
  const [syncStatus, setSyncStatus] = useState(null)
  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pages, setPages] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [sortBy, setSortBy] = useState('scanned_at')
  const [sortDir, setSortDir] = useState('desc')

  const columns = useMemo(
    () =>
      buildDetectionColumns({
        showEndpoint: true,
        showCustomer: canFilterCustomers,
      }),
    [canFilterCustomers],
  )

  useEffect(() => {
    if (canFilterCustomers) {
      fetchCustomers().then(setCustomers).catch(() => {})
    }
    fetchThreatDownSyncStatus().then(setSyncStatus).catch(() => {})
  }, [canFilterCustomers])

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(1)
    }, 350)
    return () => clearTimeout(t)
  }, [searchInput])

  const loadDetections = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const data = await fetchThreatDownDetections(
        {
          page,
          page_size: PAGE_SIZE,
          search: search || undefined,
          sort_by: sortBy,
          sort_dir: sortDir,
        },
        customerIds,
      )
      setItems(data.items)
      setTotal(data.total)
      setPages(data.pages)
    } catch {
      setError('Could not load ThreatDown detections.')
    } finally {
      setLoading(false)
    }
  }, [page, search, customerIds, sortBy, sortDir])

  useEffect(() => {
    loadDetections()
  }, [loadDetections])

  function handleSort(column) {
    if (!SORTABLE.has(column)) return
    if (sortBy === column) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(column)
      setSortDir(column === 'scanned_at' ? 'desc' : 'asc')
    }
    setPage(1)
  }

  const lastSync = syncStatus?.last_sync

  return (
    <div className="space-y-6">
      <ThreatDownNav />

      <Card>
        <CardContent className="pt-6">
          <div
            className={cn(
              'flex flex-col gap-4',
              canFilterCustomers && 'md:flex-row md:items-end md:justify-between',
            )}
          >
            {canFilterCustomers && (
              <CustomerFilter
                id="threatdown-detections-customer"
                label="Company"
                value={customerIds}
                onChange={setCustomerIds}
                customers={customers}
                className="md:min-w-[280px] md:max-w-sm"
              />
            )}
            <div className={cn('space-y-1', canFilterCustomers && 'md:text-right md:shrink-0')}>
              <div
                className={cn(
                  'flex flex-wrap items-center gap-2',
                  canFilterCustomers && 'md:justify-end',
                )}
              >
                <p className="text-sm font-medium">Sync status</p>
                {syncStatus?.running && <Badge variant="warning">Sync running</Badge>}
              </div>
              <p className="text-sm text-muted-foreground">
                {lastSync ? (
                  <>
                    Last synced {formatDateTime(lastSync.completed_at || lastSync.started_at)}
                    {lastSync.endpoints_synced != null
                      ? ` · ${lastSync.endpoints_synced} endpoints`
                      : ''}
                  </>
                ) : (
                  'No ThreatDown sync recorded yet.'
                )}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <AiQueryPanel
        scope="threatdown_detections"
        customerIds={customerIds}
        title="Ask about detections"
        description="Ask questions about ThreatDown detection events in your current scope (respects the company filter above)."
        examples={DETECTION_EXAMPLES}
      />

      <Card>
        <CardHeader>
          <CardTitle>
            Detections {total > 0 && <span className="text-muted-foreground">({total})</span>}
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Threat events from the last 90 days across your ThreatDown estate.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2 max-w-md">
            <Label htmlFor="threatdown-detections-search">Search</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="threatdown-detections-search"
                placeholder="Threat, endpoint, customer, path…"
                className="pl-9"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : !items.length ? (
            <p className="text-sm text-muted-foreground">No detections match your filters.</p>
          ) : (
            <div className="rounded-lg border border-border overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/40 text-left text-muted-foreground">
                    {columns.map((col) => (
                      <SortHeader
                        key={col.id}
                        column={col.id}
                        label={col.label}
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onSort={handleSort}
                        className={col.className}
                      />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr key={row.id} className="border-b border-border/60 last:border-0">
                      {columns.map((col) => (
                        <td
                          key={col.id}
                          className={cn(
                            'px-4 py-3',
                            col.wrap === false ? 'whitespace-nowrap' : 'whitespace-normal break-words',
                            col.className,
                          )}
                        >
                          {col.render(row)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {pages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Page {page} of {pages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= pages || loading}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
